/**
 * Deployment-license hooks (P270.4).
 *
 * `useLicenseStatus` reads `/v1/license/status` (unauthenticated on the
 * backend; works pre/post login). `useUploadLicense` POSTs a `.jwt` file
 * to the admin endpoint (multipart, so a hand-rolled fetch with the auth
 * token rather than the JSON `api` client). `isModuleLicensed` mirrors the
 * backend back-compat rule for SPA-side module visibility.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, currentAuthToken, type ApiError } from "shared/api/client";
import type { LicenseStatus } from "shared/api/types";

export const licenseKeys = {
  status: ["license", "status"] as const,
};

export function useLicenseStatus(
  /**
   * D11 -- an examiner's token is confined to /v1/examiner/*, so this
   * call could only ever 403 for them, and it fired on every render of
   * a shell they never see. The caller says when it is worth asking.
   */
  enabled = true,
): UseQueryResult<LicenseStatus, ApiError> {
  return useQuery({
    queryKey: licenseKeys.status,
    queryFn: () => api.get<LicenseStatus>("/v1/license/status"),
    enabled,
  });
}

/** P271: trigger one online refresh + revocation pass against the
 * management plane ("Sync now"). A no-op (returns the current status) when
 * no plane is configured; never errors on a plane outage. */
export function useSyncLicense(): UseMutationResult<LicenseStatus, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: () => api.post<LicenseStatus>("/v1/admin/license/sync", {}),
    onSuccess: (data) => {
      qc.setQueryData(licenseKeys.status, data);
    },
  });
}

export function useUploadLicense(): UseMutationResult<LicenseStatus, Error, File> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const token = currentAuthToken();
      const res = await fetch("/v1/admin/license", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { detail?: string };
          if (body.detail) detail = body.detail;
        } catch {
          /* non-JSON error body */
        }
        throw new Error(detail);
      }
      return (await res.json()) as LicenseStatus;
    },
    onSuccess: (data) => {
      qc.setQueryData(licenseKeys.status, data);
    },
  });
}

/**
 * SPA-side module-visibility check, mirroring the backend `licensed_module_ok`:
 * - gate off                  -> all modules visible (the estate is open)
 * - `modules` null (no claim) -> all modules visible (back-compat)
 * - otherwise                 -> strict membership
 * Fails OPEN while the status is in-flight (undefined) so the UI never
 * flickers modules away on a slow status call.
 */
export function isModuleLicensed(
  status: LicenseStatus | undefined,
  module: string,
): boolean {
  if (!status || !status.gate_enabled || status.modules === null) return true;
  return status.modules.includes(module);
}

/** The three sellable feature modules the deployment license can scope.
 * `admin` is universal (in every SKU, and holds the license panel) so it's
 * deliberately NOT gated by the license. */
export const FEATURE_MODULES = ["foundation", "twin", "framework"] as const;
export type FeatureModule = (typeof FEATURE_MODULES)[number];

/**
 * Routes the PER-USER module check must not answer for, because the
 * server deliberately answers more broadly.
 *
 * Round 11 B1. A governance ceremony is read by the people it waits on
 * -- the CRO, the business owner, the AE, and the second-line reviewer
 * whose challenge is quoted on the record. `require_ceremony_reader`
 * has admitted all of them since round 10 A4, for exactly this reason.
 * The chrome then learned the per-user module set and started refusing
 * the reviewer at the door: his own home carries a link to the MVB
 * declaration his challenge is on, and following it told him the
 * register was nobody's business of his.
 *
 * The module gate itself is right and stays. What was wrong is a
 * client-side copy of a server rule being STRICTER than the rule --
 * the drift AdminListView already names in its own comment. These
 * paths attempt the read and render whatever the server answers: the
 * record, or the server's own refusal.
 *
 * Deliberately narrow: ceremony records only, matched by prefix. The
 * foundation REGISTERS (services, capabilities, vendors) are a
 * different question and stay gated.
 */
const SERVER_GATED_PREFIXES = [
  "/foundation/mvb",
  // Round 11 B2 -- the same shape on the register the six-layer block
  // now links the compliance analyst to. `require_data_asset_reader`
  // admits her; the chrome refused her at the door, so the product
  // taught her which layer was hers and then shut the door on it.
  "/foundation/admin/data-assets",
] as const;

export function isServerGatedPath(pathname: string): boolean {
  return SERVER_GATED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/** The feature module a path belongs to (its first segment), or null for
 * `/`, the shared exec views, `/admin/*` -- none of which are
 * license-gated -- and the ceremony records above, which the server
 * gates itself. Used to render `ModuleNotLicensed` on a deep-link into
 * an unlicensed module. */
export function featureModuleForPath(pathname: string): FeatureModule | null {
  if (isServerGatedPath(pathname)) return null;
  const seg = pathname.split("/").filter(Boolean)[0];
  return seg && (FEATURE_MODULES as readonly string[]).includes(seg)
    ? (seg as FeatureModule)
    : null;
}
